import { ReactElement } from 'react';
import { Heading } from '../../../components/Heading';
import { Paragraph } from '../../../components/Paragraph';
import { ContainerCard } from '../../../components/ContainerCard/ContainerCard';
import classes from './PartnerLeadsOverviewPage.module.css';
import { Alert } from '../../../components/Alert/Alert';
import { GhostLoader } from '../../../components/GhostLoader/GhostLoader';
import { useGetPartnerLeadsOverviewQuery } from '../../../hooks/queries/useGetPartnerLeadsOverviewQuery';
import { formatNumber } from '../../../utils/numberFormatters';
import { WalletIcon, PersonGroupIcon, TasklistIcon } from '@navikt/aksel-icons';
import type { PartnerLeadsTopPartner } from '../../../api/queries';

const STATS = [
  { id: 'totalPartners', key: 'totalPartners', title: 'Totalt antall partnere', meta: 'med aktive leads', icon: PersonGroupIcon },
  { id: 'totalLeads', key: 'totalLeads', title: 'Totalt antall leads', meta: 'på tvers av alle partnere', icon: TasklistIcon },
  { id: 'pipelineMonth', key: 'totalPipelineValueNOK', title: 'Total pipeline-verdi', meta: 'per måned', icon: WalletIcon, formatKr: true },
  { id: 'pipelineYear', key: 'totalPipelineValueNOK', title: 'Total pipeline-verdi', meta: 'per år', icon: WalletIcon, formatKr: true, multiplier: 12 },
  { id: 'totalCashback', key: 'totalCashbackValueNOK', title: 'Total cashback-verdi', meta: 'per måned', icon: WalletIcon, formatKr: true },
] as const;

const LEAD_STATUS_LABELS: Record<string, string> = {
  NEW: 'Ny lead',
  CONTACTED: 'Kontaktet',
  QUALIFIED: 'Kvalifisert',
  PROPOSAL_SENT: 'Tilbud sendt',
  NEGOTIATING: 'Forhandling',
  WON: 'Vunnet',
  LOST: 'Tapt',
  ON_HOLD: 'På vent',
};

const translateStatus = (status: string) => LEAD_STATUS_LABELS[status] || status;
const formatKrValue = (value: number) => `${formatNumber(value)} kr/mnd`;
const calculatePercentage = (value: number, total: number) => total > 0 ? (value / total) * 100 : 0;

const EmptyState = ({ message }: { message: string }) => (
  <div className={classes.emptyState}>
    <Paragraph>{message}</Paragraph>
  </div>
);

const StatCard = ({ stat, value }: { stat: typeof STATS[number]; value: number }) => {
  const Icon = stat.icon;
  const multipliedValue = 'multiplier' in stat ? value * stat.multiplier : value;
  const displayValue = stat.formatKr ? `${formatNumber(multipliedValue)} kr` : multipliedValue;

  return (
    <ContainerCard>
      <ContainerCard.Title icon={<Icon />}>{stat.title}</ContainerCard.Title>
      <ContainerCard.Body className={classes.statBody}>
        <div className={classes.statValue}>{displayValue}</div>
        <div className={classes.statMeta}>{stat.meta}</div>
      </ContainerCard.Body>
    </ContainerCard>
  );
};

const StatusItem = ({ status, totalValue }: { status: { status: string; count: number; valueNOK: number }; totalValue: number }) => {
  const percentage = calculatePercentage(status.valueNOK, totalValue);

  return (
    <div className={classes.statusItem}>
      <div className={classes.statusHeader}>
        <span className={classes.statusName}>{translateStatus(status.status)}</span>
        <span className={classes.statusPercentage}>{percentage.toFixed(0)}%</span>
      </div>
      <div className={classes.statusMetrics}>
        <div className={classes.statusCount}>{status.count} leads</div>
        <div className={classes.statusValue}>{formatKrValue(status.valueNOK)}</div>
      </div>
      <div className={classes.statusBar}>
        <div className={classes.statusBarFill} style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
};

const MetricBox = ({ label, value }: { label: string; value: string }) => (
  <div className={classes.metricBox}>
    <div className={classes.metricLabel}>{label}</div>
    <div className={label === 'Pipeline' ? classes.metricValue : classes.metricCashback}>{value}</div>
  </div>
);

const PartnerCard = ({ partner, rank }: { partner: PartnerLeadsTopPartner; rank: number }) => (
  <div className={classes.partnerCard}>
    <div className={classes.partnerMain}>
      <div className={classes.partnerHeader}>
        <div className={classes.partnerRank}>#{rank}</div>
        <div className={classes.partnerInfo}>
          <div className={classes.partnerName}>{partner.partnerName}</div>
          <div className={classes.partnerLeadCount}>{partner.totalLeads} leads</div>
        </div>
      </div>
      <div className={classes.partnerMetrics}>
        <MetricBox label="Pipeline" value={formatKrValue(partner.pipelineValueNOK)} />
        <div className={classes.metricDivider} />
        <MetricBox label="Cashback" value={formatKrValue(partner.cashbackValueNOK)} />
      </div>
    </div>
    {partner.statusBreakdown.length > 0 && (
      <div className={classes.partnerFooter}>
        {partner.statusBreakdown.map((status) => (
          <span key={status.status} className={classes.statusTag}>
            {translateStatus(status.status)}: {status.count}
          </span>
        ))}
      </div>
    )}
  </div>
);

export const PartnerLeadsOverviewPage = (): ReactElement => {
  const { data, isLoading, error } = useGetPartnerLeadsOverviewQuery();

  if (isLoading) return <GhostLoader />;
  if (error) return <Alert variant="danger" size="md"><Paragraph>Kunne ikke laste pipeline data. Prøv igjen senere.</Paragraph></Alert>;
  if (!data) return null;

  const activeStatuses = data.statusDistribution.filter((s) => s.count > 0).sort((a, b) => b.valueNOK - a.valueNOK);
  const totalPipelineValue = activeStatuses.reduce((sum, s) => sum + s.valueNOK, 0);

  return (
    <div className={classes.container}>
      <header className={classes.header}>
        <Heading as="h1">Partner pipeline oversikt</Heading>
        <Paragraph size="lg">Aggregert pipeline-statistikk på tvers av alle partnere</Paragraph>
      </header>

      <div className={classes.content}>
        <div className={classes.statsGrid}>
          {STATS.map((stat) => <StatCard key={stat.id} stat={stat} value={data[stat.key]} />)}
        </div>

        <ContainerCard>
          <ContainerCard.Title>Status-fordeling</ContainerCard.Title>
          <ContainerCard.Body>
            {activeStatuses.length === 0 ? (
              <EmptyState message="Ingen aktive leads funnet." />
            ) : (
              <div className={classes.statusGrid}>
                {activeStatuses.map((status) => (
                  <StatusItem key={status.status} status={status} totalValue={totalPipelineValue} />
                ))}
              </div>
            )}
          </ContainerCard.Body>
        </ContainerCard>

        <ContainerCard>
          <ContainerCard.Title>Top 10 partnere etter pipeline-verdi</ContainerCard.Title>
          <ContainerCard.Body>
            {data.topPartners.length === 0 ? (
              <EmptyState message="Ingen partnere med leads funnet." />
            ) : (
              <div className={classes.partnerList}>
                {data.topPartners.map((partner, index) => (
                  <PartnerCard key={partner.partnerId} partner={partner} rank={index + 1} />
                ))}
              </div>
            )}
          </ContainerCard.Body>
        </ContainerCard>
      </div>
    </div>
  );
};
